import React, {useState, useMemo, useCallback, useContext, createContext} from 'react'
import ButtonLoader from 'js/studios/shared/button_loader'
import {connect} from 'react-redux'
import {loginFacebook, sendRequest, GoogleLoginButton, googleLoginAvailable, AppleSigninButton, appleSigninAvailable} from 'js/studios/shared/reg/auth'
import {FBButton, SignIn} from 'js/studios/shared/reg/panels/sign_in'
import {api, apiErrorMessage} from 'js/studios/shared/api'
import {trackEventSP} from 'js/studios/shared/tracking'
import {Frame, FrameHeader} from 'js/studios/shared/flows'
import {TIMELINE_FALLBACK_LEAD_COEFFICIENT, TIMELINE_CHOICES, SHIPPING_COUNTRY_CHOICES, RECIPIENT_CHOICES, INTENT_CHOICES,
        CATEGORY_COEFFICIENTS, CATEGORY_FALLBACK_COEFFICIENT} from './data'
import {SHOULDER_DESIGNS, SIGNET_SHOULDER_DESIGNS_FALLBACK_LEAD_COEFFICIENT} from './signet'
import {LinkFormattedText} from 'js/studios/shared/utils'
import {KeyValueCtx} from 'js/studios/shared/unredux/contexts/key_value'
import * as Sentry from "@sentry/react"

function isMobile(){
  return window.screen.width < 480
}

function isFacebookAppBrowser() {
  return !navigator?.userAgent || navigator.userAgent.includes("FB_IAB/")
}

function isEngagement(projData) {
  return projData.flow === 'engagement' || projData.categoryTop === 'engagement'
}

function isSignet(projData) {
  return projData.categorySecondary === "signet_ring" || projData.sourceCategory === 'signet_ring'
}

function isNecklace(projData) {
  return projData.categorySecondary === "necklace" || projData.sourceCategory === 'necklace'
}

function isClassRing(projData) {
  return projData.categorySecondary === "class_ring" || projData.sourceCategory === 'class_ring'
}

function generateLeadValue(projData) {
  let budgetCoeff = 1, mobileCoeff, timelineCoeff = 1, recipientIntentCoeff = 1, shippingCountryCoeff = 1,
      shoulderDesignCoeff = 1, detailsLengthCoeff = 1, attachmentsCoeff = 1, categoryCoeff = 1

  if (isEngagement(projData)) {
    // maximum budget
    const budgetMax = projData.budgetMax
    if (!budgetMax) {
      budgetCoeff = 25
    } else {
      const budgetMaxNumeric = parseInt(budgetMax)
      if (budgetMax === '24000+') {
        budgetCoeff = 10
      } else if (budgetMaxNumeric < 1000) {
        budgetCoeff = 15
      } else if (budgetMaxNumeric < 1500) {
        budgetCoeff = 18
      } else if (budgetMaxNumeric < 2500) {
        budgetCoeff = 33
      } else if (budgetMaxNumeric < 15000) {
        budgetCoeff = 45
      } else if (budgetMaxNumeric <= 24000) {
        budgetCoeff = 70
      }
    }

    // recipient and purchase intent, combined
    const recipient = projData.recipient
    const intent = projData.intent
    const selectedRecipientChoice = RECIPIENT_CHOICES.find(c => c.value === recipient)
    let recipientCoeff = 1
    if (selectedRecipientChoice) {
      recipientCoeff = selectedRecipientChoice.lead_coefficient
    }
    const selectedIntentChoice = INTENT_CHOICES.find(c => c.value === intent)
    let intentCoeff = 1
    if (selectedIntentChoice) {
      intentCoeff = selectedIntentChoice.lead_coefficient
    }
    recipientIntentCoeff = recipientCoeff * intentCoeff

    // "when do you need this"
    const timeline = projData.timeline
    const selectedTimelineChoice = TIMELINE_CHOICES.find(c => c.value === timeline)
    if (selectedTimelineChoice) {
      timelineCoeff = selectedTimelineChoice.lead_coefficient
    } else {
      timelineCoeff = TIMELINE_FALLBACK_LEAD_COEFFICIENT
    }
  } else {
    // base value per category
    const category = projData.categorySecondary
    if (category in CATEGORY_COEFFICIENTS){
      categoryCoeff = CATEGORY_COEFFICIENTS[category]
    } else {
      categoryCoeff = CATEGORY_FALLBACK_COEFFICIENT
    }

    // length of input in the details field
    if (typeof projData.details  === 'string'){
      const detailsLength = projData.details.length
      if (detailsLength < 10) {
        detailsLengthCoeff = isSignet(projData) ? 0.4 : 0.1
      } else if (detailsLength < 50) {
        detailsLengthCoeff = isSignet(projData) ? 0.5 : 0.4
      } else if (detailsLength < 100) {
        detailsLengthCoeff = isSignet(projData) ? 0.9 : 0.6
      } else if (detailsLength < 200) {
        detailsLengthCoeff = 1.3
      } else {
        detailsLengthCoeff = isSignet(projData) ? 2.6 : 2.7
      }
    }

    // were any attachments uploaded to the details form?
    if (projData.attachments?.length) {
      attachmentsCoeff = isSignet(projData) ? 1.3 : 1.4
    } else {
      attachmentsCoeff = isSignet(projData) ? 0.7 : 0.6
    }

    if (isSignet(projData)){
      // include designs on the signet ring shoulders?
      const shoulderDesigns = projData.shoulderDesigns
      const selectedShoulderDesignChoice = SHOULDER_DESIGNS.find(c => c.name === shoulderDesigns)
      if (selectedShoulderDesignChoice) {
        shoulderDesignCoeff = selectedShoulderDesignChoice.lead_coefficient
      } else {
        shoulderDesignCoeff = SIGNET_SHOULDER_DESIGNS_FALLBACK_LEAD_COEFFICIENT
      }
    }
  }

  // shipping destination country
  // (not applying shipping country coefficient to engagement ring projects for now)
  const shippingCountry = projData.shippingLocation
  const selectedShippingCountryChoice = SHIPPING_COUNTRY_CHOICES.find(c => c.value === shippingCountry)
  if (selectedShippingCountryChoice) {
    if (isEngagement(projData)){
      shippingCountryCoeff = selectedShippingCountryChoice.engagement_lead_coefficient
    } else if (isSignet(projData)){
      shippingCountryCoeff = selectedShippingCountryChoice.signet_lead_coefficient
    } else {
      shippingCountryCoeff = selectedShippingCountryChoice.other_lead_coefficient
    }
  } else {
    shippingCountryCoeff = 1
  }

  // mobile device (based on device screen size)?
  if (isMobile()){
    if (isEngagement(projData)){
      mobileCoeff = 0.9
    } else if(isSignet(projData)){
      mobileCoeff = 0.8
    } else{
      mobileCoeff = 0.8
    }
  } else {
    if (isEngagement(projData)){
      mobileCoeff = 1.7
    } else if(isSignet(projData)){
      mobileCoeff = 1.5
    } else{
      mobileCoeff = 1.4
    }
  }

  const spamCoef = isFacebookAppBrowser() ? 0.2 : 1.0

  return (budgetCoeff * mobileCoeff * timelineCoeff * recipientIntentCoeff *
      shippingCountryCoeff * shoulderDesignCoeff * detailsLengthCoeff *
      attachmentsCoeff * categoryCoeff * spamCoef)
}

function projFormType(projData) {
  if (isEngagement(projData)) {
    return 'engagement'
  } else if (isSignet(projData)) {
    return 'signet'
  } else if (isNecklace(projData)) {
    return 'pendant'
  } else if (isClassRing(projData)) {
    return 'class_ring'
  }
  return 'other'
}

function regTrackSubmit(name, success, reason_for_fail, email) {
  trackEventSP(name, 'Customer sign up', {
    'success': success,
    'reason_for_fail': reason_for_fail,
    'email_address': email
  })
}

// context & wrapper component that provides the submitProject function to children via context
const ProjectSubmissionCtx = createContext()
ProjectSubmissionCtx.displayName = "ProjectSubmissionCtx"
function ProjectSubmissionProvider({children}) {
  const {data} = useContext(KeyValueCtx)
  const ctxVal = useMemo(() => ({
    submitProject: () => {
      const postBody = {
        ...data,
        attachments: (data.attachments || []).map(a => a.id),
        formType: projFormType(data),
        legacyLeadValue: generateLeadValue(data),
      }

      return api.post(window.cm.urls['create-project-api'], postBody).then(resp => {
        window.location.href = resp.url
      }).catch(err => {
        const errMsg = apiErrorMessage(err)
        trackEventSP('Project submitted', 'Create a project', {
          'error_message': errMsg,
          'post_id': null
        })

        if (err.httpStatus) {
          Sentry.withScope(scope => {
            scope.setExtras({error: errMsg, data: data, status: err.httpStatus})
            Sentry.captureMessage(new Error('Could not submit project'))
          })
        }

        throw errMsg
      })
    },
  }), [data])

  return <ProjectSubmissionCtx.Provider value={ctxVal}>{children}</ProjectSubmissionCtx.Provider>
}

class LoggedIn extends React.Component {
  static contextType = ProjectSubmissionCtx
  state = {loading: false, error: ""}

  onSubmit = ev => {
    ev.preventDefault()
    if (!this.state.loading) {
      this.setState({loading: true})
      this.context.submitProject().catch(errorMessage => {
        this.setState({loading: false, error: errorMessage})
      })
    }
  }

  componentDidMount() {
    trackEventSP('Sign up or log in modal displayed', 'Customer sign up', {
      'source': 'post-creation',
      'panel': 'already-logged-in',
    })
  }

  render() {
    return <Frame onSubmit={this.onSubmit} cta={false}>
      <FrameHeader>Let’s get started!</FrameHeader>
      <p className="txt--center mb--lg">You are now ready to start your design experience.</p>
      {this.state.error && <p className="flow-error">{this.state.error}</p>}
      <div className="btn-group btn-group--stack field--md">
        <ButtonLoader type="submit" className="btn btn--lg" loading={this.state.loading}>Let’s go!</ButtonLoader>
      </div>
    </Frame>
  }
}

class ChooseMethod extends React.Component {
  static contextType = ProjectSubmissionCtx
  state = {fbLoading: false, error: ""}

  signInFacebook = ev => {
    ev.preventDefault()
    if (this.state.loading || this.state.fbLoading) {
      return false
    }
    this.setState({fbLoading: true})

    loginFacebook()
      .then(resp => {
        regTrackSubmit('Customer Facebook sign up submitted', true, '', resp.email)
        this.context.submitProject().catch(errorMessage => {
          this.setState({fbLoading: false, error: errorMessage})
        })
      })
      .catch(errorMessage => {
        regTrackSubmit('Customer Facebook sign up submitted', false, errorMessage, '')
        this.setState({fbLoading: false, error: errorMessage})
      })
  }

  signInGoogle = result => {
    if (result.error) {
      regTrackSubmit('Customer Google sign up submitted', false, result.error, '')
      this.setState({error: result.error})
    } else {
      regTrackSubmit('Customer Google sign up submitted', true, '', result.email)
      return this.context.submitProject().catch(errMsg => this.setState({error: errMsg}))
    }
  }

  signInApple = result => {
    if (result.error) {
      regTrackSubmit('Customer Apple sign up submitted', false, result.error, '')
      this.setState({error: result.error})
    } else {
      regTrackSubmit('Customer Apple sign up submitted', true, '', result.email)
      return this.context.submitProject().catch((errMsg) => this.setState({error: errMsg}))
    }
  }

  componentDidMount(){
    trackEventSP('Sign up or log in modal displayed', 'Customer sign up', {
      'source': 'post-creation',
      'panel': 'registration',
    })
  }

  render() {
    return <div className="anim--fade-in">
      <div className="btn-group btn-group--stack field--md mb--1">
        <button type="button" className="btn btn--lg" disabled={this.state.fbLoading} tabIndex="0" onClick={this.props.switchToSignUp}>Continue with email</button>
      </div>
      <p className="txt--center txt--body-2 mb--2">
        Already have an account? <a tabIndex="0" href="#" onClick={this.props.switchToSignIn}><b>Sign in</b></a>
      </p>
      <p className="txt--center txt--red txt--overline mb--2">or</p>
      <div className="btn-group btn-group--stack field--md">
          {googleLoginAvailable() && <GoogleLoginButton handler={this.signInGoogle} signup={true} />}
          {appleSigninAvailable() && <AppleSigninButton handler={this.signInApple} signup={true} />}
          <FBButton loading={this.state.fbLoading} onClick={this.signInFacebook} signup={true} />
      </div>
    </div>
  }
}

@connect(state => ({user: state.user}))
class EmailSignUp extends React.Component {
  static contextType = ProjectSubmissionCtx
  state = {
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    passwordConfirm: "",
    loading: false,
    error: ""
  }

  isValid() {
    if (this.state.password !== this.state.passwordConfirm){
      this.setState({error: "Passwords do not match. Please try again."})
      return false
    } else if (this.state.firstName.length === 0 || this.state.lastName.length === 0 || this.state.email.length === 0 || this.state.password.length === 0){
      this.setState({error: "Please fill out all fields before continuing."})
      return false
    }
    return true
  }

  signUp = ev => {
    ev.preventDefault()
    if (!this.state.loading && this.isValid()) {
      this.setState({error: "", loading: true})

      // If registration was successful but not project creation, sometimes we
      // can have a logged in user submitting...
      if (this.props.user.id) {
        this.context.submitProject().catch(errorMessage => {
          this.setState({loading: false, error: errorMessage})
        })
      } else {
        const data = {
          email: this.state.email,
          password: this.state.password,
          firstName: this.state.firstName,
          lastName: this.state.lastName,
          agreement: true,
        }
        sendRequest('signupFull', data)
          .then(resp => {
            regTrackSubmit('Customer email sign up submitted', true, '', resp.email)
            this.context.submitProject().catch(errorMessage => {
              this.setState({loading: false, error: errorMessage})
            })
          })
          .catch(errorMessage => {
            regTrackSubmit('Customer email sign up submitted', false, errorMessage,  data.email)
            this.setState({loading: false, error: errorMessage})
          })
        }
    }
  }

  updateField(field, ev) {
    this.setState({[field]: ev.target.value})
  }

  componentDidMount() {
    trackEventSP('Sign up or log in modal displayed', 'Customer sign up', {
      'source': 'post-creation',
      'panel': 'email-registration'
    })
  }

  render() {
    return <form className="flex-col anim--fade-in" onSubmit={this.signUp}>
      <fieldset className="field--md mb--1">
        <label className="field--md">
          <input type="text" aria-label="First Name" placeholder="First Name" onChange={this.updateField.bind(this, 'firstName')} value={this.state.firstName} />
        </label>
        <label className="field--md">
          <input type="text" aria-label="Last Name" placeholder="Last Name" onChange={this.updateField.bind(this, 'lastName')} value={this.state.lastName} />
        </label>
        <label className="field--md">
          <input type="email" aria-label="Email" placeholder="Email" onChange={this.updateField.bind(this, 'email')} value={this.state.email} />
        </label>
        <label className="field--md">
          <input type="password" aria-label="Password" placeholder="Password" onChange={this.updateField.bind(this, 'password')} value={this.state.password} />
        </label>
        <label className="field--md">
          <input type="password" aria-label="Confirm password" placeholder="Confirm password" onChange={this.updateField.bind(this, 'passwordConfirm')} value={this.state.passwordConfirm} />
        </label>
      </fieldset>
      {this.state.error && <div className="flow-error"><LinkFormattedText>{this.state.error}</LinkFormattedText></div>}
      <div className="btn-group btn-group--stack field--md">
        <ButtonLoader type="submit" className="btn btn--lg btn--arrow-right" loading={this.state.loading}>Continue</ButtonLoader>
      </div>
      <p className="txt--center txt--body-2 mt--1_25 mb--1__25">
        <a href="#" tabIndex="0" onClick={this.props.switchToChooseMethod}><b>Back to sign-up options</b></a>
      </p>
      <p className="txt--center txt--body-2">
        Already have an account? <a href="#" tabIndex="0" onClick={this.props.switchToSignIn}><b>Sign In</b></a>
      </p>
    </form>
  }
}

function SignUp({switchToSignIn}) {
  const [frame, setFrame] = useState("choose")

  const
    switchToSignUp = useCallback(ev => { ev.preventDefault(); setFrame("signup") }, []),
    switchToChooseMethod = useCallback(ev => { ev.preventDefault(); setFrame("choose") }, [])

  return <div className="flow-frame anim--fade-in">
    <div className="flow-frame__body">
      <FrameHeader>Let’s get started!</FrameHeader>
      <p className="txt--center mb--lg">You are now ready to start your design experience.</p>
      {frame === 'choose'
        ? <ChooseMethod switchToSignUp={switchToSignUp} switchToSignIn={switchToSignIn} />
        : <EmailSignUp switchToSignIn={switchToSignIn} switchToChooseMethod={switchToChooseMethod} />
      }
    </div>
  </div>
}

function signInEventName(method) {
  switch (method) {
    case "login":
      return "Customer logs in"
    case "facebook":
      return "Customer Facebook sign up submitted"
    case "google":
      return "Customer Google sign up submitted"
    case "apple":
      return "Customer Apple sign up submitted"
    case "resetPassword":
      return "Customer resets password"
    default:
      return "Customer logs in via unknown method"
  }
}

function SignInWrapper({switchToSignUp}) {
  const {submitProject} = useContext(ProjectSubmissionCtx)

  const
    onSuccess = useCallback(({method, resp}) => {
      regTrackSubmit(signInEventName(method), true, '', resp.email)
      if (method !== "resetPassword") {
        return submitProject()
      }
    }, [submitProject]),
    onFail = useCallback(({method, email, error}) => {
      regTrackSubmit(signInEventName(method), false, error, email)
    }, [])

  return <SignIn analyticsSource="project-creation" switchToSignUp={switchToSignUp} onSuccess={onSuccess} onFail={onFail} />
}

@connect(state => ({user: state.user}))
export class Registration extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      frame: props.user.id ? 'logged-in': 'signup'
    }
  }

  setFrame(frame, _e) {
    this.setState({frame: frame})
  }

  get bodyComponent() {
    if (this.state.frame === 'logged-in') {
      return <LoggedIn />
    } else if (this.state.frame === 'signup') {
      return <SignUp switchToSignIn={this.setFrame.bind(this, 'signin')} />
    }
    return <SignInWrapper switchToSignUp={this.setFrame.bind(this, 'signup')} />
  }

  render() {
    return <ProjectSubmissionProvider>{this.bodyComponent}</ProjectSubmissionProvider>
  }
}
